import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const STATUS_TABS = [
  { key: 'active', label: 'Working', icon: 'fa-bolt', tone: 'bg-emerald-500' },
  { key: 'replied', label: 'Replied', icon: 'fa-comment-dots', tone: 'bg-sky-500' },
  { key: 'booked', label: 'Booked', icon: 'fa-calendar-check', tone: 'bg-violet-500' },
  { key: 'paused', label: 'Paused', icon: 'fa-pause', tone: 'bg-amber-500' },
  { key: 'stopped', label: 'Opted out', icon: 'fa-ban', tone: 'bg-rose-500' },
  { key: 'completed', label: 'Finished', icon: 'fa-flag-checkered', tone: 'bg-slate-500' },
];

const CHANNEL_ICON = { sms: 'fa-comment-sms', email: 'fa-envelope', call_task: 'fa-phone-volume' };

export default function SdrAutopilot() {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState('active');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [convo, setConvo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, e] = await Promise.all([
        api.get('/api/sdr/stats'),
        api.get('/api/sdr/config'),
        api.get('/api/sdr/enrollments', { params: { status: tab, limit: 150 } }),
      ]);
      setStats(s.data);
      setConfig(c.data);
      setRows(e.data.enrollments || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the AI SDR');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async (patch) => {
    try {
      const res = await api.put('/api/sdr/config', patch);
      setConfig(res.data);
      toast.success('AI SDR updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Only managers can change the SDR');
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post('/api/sdr/run', { limit: 25 });
      if (!res.data.ran) toast.error(res.data.reason);
      else toast.success(`Worked ${res.data.processed} touch${res.data.processed === 1 ? '' : 'es'}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const enroll = async (includeUncontactable = false) => {
    setEnrolling(true);
    try {
      const res = await api.post('/api/sdr/enroll', { auto: true, limit: 25, include_uncontactable: includeUncontactable });
      if (res.data.enrolled) toast.success(`${res.data.enrolled} lead${res.data.enrolled === 1 ? '' : 's'} enrolled`);
      else toast(res.data.note || 'No new leads matched the rules', { icon: 'ℹ️', duration: 7000 });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  const openConvo = async (id) => {
    try {
      const res = await api.get(`/api/sdr/conversation/${id}`);
      setConvo(res.data);
      setReplyText('');
    } catch (err) {
      toast.error('Could not open the conversation');
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/api/sdr/enrollments/${id}/${action}`);
      toast.success(action === 'advance' ? 'Next touch sent' : `Sequence ${action}d`);
      loadAll();
      if (convo) openConvo(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const res = await api.post(`/api/sdr/enrollments/${convo.enrollment.id}/reply`, { text: replyText });
      toast.success(`Intent: ${res.data.intent.replace('_', ' ')}${res.data.booked ? ' — appointment booked' : ''}`);
      openConvo(convo.enrollment.id);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reply handling failed');
    } finally {
      setReplying(false);
    }
  };

  const c = stats?.counts || {};

  return (
    <div className="space-y-6" data-testid="sdr-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Automation</p>
          <h1 className="text-3xl font-black tracking-tight text-text-primary">AI SDR Autopilot</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Every qualified lead gets worked without a rep lifting a finger — a seven-touch text and email cadence
            written per homeowner, inbound replies read and answered, and appointments booked straight onto the calendar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => enroll(false)} disabled={enrolling}
            className="px-5 py-2.5 rounded-xl bg-surface border border-border text-sm font-bold text-text-primary hover:border-primary transition-all disabled:opacity-60"
            data-testid="sdr-enroll-btn">
            {enrolling ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Enrolling…</> : <><i className="fa-solid fa-user-plus mr-2" />Enroll next 25</>}
          </button>
          <button onClick={() => enroll(true)} disabled={enrolling}
            className="px-5 py-2.5 rounded-xl bg-surface border border-border text-sm font-bold text-text-muted hover:border-primary transition-all disabled:opacity-60"
            data-testid="sdr-enroll-drafts-btn">
            <i className="fa-solid fa-pen-clip mr-2" />Queue drafts anyway
          </button>
          <button onClick={runNow} disabled={running}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold shadow hover:shadow-lg transition-all disabled:opacity-60"
            data-testid="sdr-run-btn">
            {running ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Working the queue…</> : <><i className="fa-solid fa-play mr-2" />Run now</>}
          </button>
        </div>
      </div>

      {stats?.delivery_mode === 'simulated' && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="sdr-simulated-banner">
          <i className="fa-solid fa-triangle-exclamation" />
          <span>
            <b>SIMULATED sending.</b> Copy is written and logged for real, but nothing leaves the building until a
            Twilio (SMS) or SendGrid/Resend (email) key is added in <Link to="/settings" className="underline font-bold">Settings</Link>.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sdr-stats">
        {[
          { label: 'In sequence', value: c.active || 0, icon: 'fa-bolt', tone: 'bg-emerald-500' },
          { label: 'Touches sent', value: stats?.touches_sent || 0, icon: 'fa-paper-plane', tone: 'bg-sky-500' },
          { label: 'Replies in', value: stats?.replies_in || 0, icon: 'fa-comment-dots', tone: 'bg-indigo-500' },
          { label: 'Reply rate', value: `${stats?.reply_rate || 0}%`, icon: 'fa-percent', tone: 'bg-violet-500' },
          { label: 'Booked', value: c.booked || 0, icon: 'fa-calendar-check', tone: 'bg-fuchsia-500' },
          { label: 'Due right now', value: stats?.due_now || 0, icon: 'fa-hourglass-half', tone: 'bg-amber-500' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-2xl border border-border bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-6 h-6 rounded-lg ${k.tone} flex items-center justify-center`}>
                <i className={`fa-solid ${k.icon} text-white text-[10px]`} />
              </span>
              <span className="text-2xl font-black text-text-primary">{k.value}</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-text-muted">{k.label}</p>
          </div>
        ))}
      </div>

      {config && (
        <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="sdr-config">
          <div className="flex flex-wrap items-center gap-6">
            <button onClick={() => saveConfig({ enabled: !config.enabled })}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${config.enabled ? 'bg-emerald-500 text-white' : 'bg-surface-alt border border-border text-text-muted'}`}
              data-testid="sdr-toggle-enabled">
              <i className={`fa-solid ${config.enabled ? 'fa-circle-check' : 'fa-circle-pause'} mr-2`} />
              {config.enabled ? 'Autopilot ON' : 'Autopilot OFF'}
            </button>
            {[
              { key: 'auto_enroll', label: 'Auto-enroll new leads' },
              { key: 'auto_reply', label: 'Auto-answer replies' },
              { key: 'auto_book', label: 'Self-book appointments' },
              { key: 'respect_business_hours', label: 'Business hours only' },
            ].map(t => (
              <label key={t.key} className="flex items-center gap-2 text-sm font-semibold text-text-primary cursor-pointer">
                <input type="checkbox" checked={!!config[t.key]} onChange={e => saveConfig({ [t.key]: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500" data-testid={`sdr-${t.key}`} />
                {t.label}
              </label>
            ))}
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Min score</label>
              <input type="number" min="0" max="100" defaultValue={config.min_score}
                onBlur={e => saveConfig({ min_score: Number(e.target.value) })}
                className="input-field w-20" data-testid="sdr-min-score" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Daily cap</label>
              <input type="number" min="1" max="2000" defaultValue={config.daily_cap}
                onBlur={e => saveConfig({ daily_cap: Number(e.target.value) })}
                className="input-field w-24" data-testid="sdr-daily-cap" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="sdr-cadence">
            {(config.cadence || []).map(s => (
              <div key={s.step} className="px-3 py-2 rounded-xl bg-surface-alt border border-border">
                <p className="text-[11px] font-black text-text-primary">
                  <i className={`fa-solid ${CHANNEL_ICON[s.channel]} mr-1.5 text-primary`} />
                  #{s.step} {s.channel === 'call_task' ? 'rep call' : s.channel}
                </p>
                <p className="text-[10px] text-text-muted">{s.delay_hours === 0 ? 'immediately' : `+${s.delay_hours}h`}</p>
              </div>
            ))}
            <p className="text-[11px] text-text-muted self-center max-w-xs ml-2">
              Runs every 15 minutes on its own. Last pass {stats?.last_run ? `${stats.last_run.slice(11, 16)} UTC — ${stats.last_run_processed} touches` : 'not yet'}.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="sdr-tabs">
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${tab === t.key ? 'border-primary bg-surface shadow-sm text-text-primary' : 'border-border bg-surface-alt text-text-muted hover:border-primary/40'}`}
            data-testid={`sdr-tab-${t.key}`}>
            <i className={`fa-solid ${t.icon} mr-2`} />{t.label}
            <span className="ml-2 text-[11px] font-black">{c[t.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading the queue…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center" data-testid="sdr-empty">
            <i className="fa-solid fa-robot text-3xl text-text-muted mb-3" />
            <p className="text-sm text-text-muted">Nothing here yet. Hit <b>Enroll next 25</b> to put your best leads into the cadence.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-[10px] uppercase tracking-widest text-text-muted">
              <tr>
                <th className="text-left px-5 py-3">Homeowner</th>
                <th className="text-left px-3 py-3">Reach</th>
                <th className="text-left px-3 py-3">Score</th>
                <th className="text-left px-3 py-3">Touches</th>
                <th className="text-left px-3 py-3">Next</th>
                <th className="text-left px-3 py-3">Signal</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border hover:bg-surface-alt/60" data-testid={`sdr-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/leads/${r.lead_id}`} className="font-bold text-text-primary hover:text-primary">{r.lead_name}</Link>
                    <p className="text-[11px] text-text-muted">{r.address}{r.city ? `, ${r.city}` : ''} {r.state}</p>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-text-muted">
                    {r.phone ? <span className="block"><i className="fa-solid fa-phone mr-1" />{r.phone}</span> : null}
                    {r.email ? <span className="block truncate max-w-[160px]"><i className="fa-solid fa-envelope mr-1" />{r.email}</span> : null}
                    {!r.phone && !r.email ? '—' : null}
                  </td>
                  <td className="px-3 py-3 font-black text-text-primary">{r.score || '—'}</td>
                  <td className="px-3 py-3">{r.touches || 0}{r.replies ? <span className="ml-2 text-[10px] font-bold text-sky-600">{r.replies} reply</span> : null}</td>
                  <td className="px-3 py-3 text-[11px] text-text-muted">{r.next_run_at ? `${r.next_run_at.slice(5, 10)} ${r.next_run_at.slice(11, 16)}` : '—'}</td>
                  <td className="px-3 py-3 text-[11px] text-text-muted">
                    {r.intent ? <span className="px-2 py-0.5 rounded-lg bg-sky-100 text-sky-700 font-bold">{r.intent.replace('_', ' ')}</span> : (r.last_channel || '—')}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openConvo(r.id)} className="px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-[11px] font-bold hover:border-primary" data-testid={`sdr-open-${r.id}`}>
                      <i className="fa-solid fa-comments mr-1.5" />Thread
                    </button>
                    {r.status === 'active' && (
                      <button onClick={() => act(r.id, 'pause')} className="ml-2 px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-[11px] font-bold hover:border-amber-400" data-testid={`sdr-pause-${r.id}`}>
                        <i className="fa-solid fa-pause" />
                      </button>
                    )}
                    {r.status === 'paused' && (
                      <button onClick={() => act(r.id, 'resume')} className="ml-2 px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-[11px] font-bold hover:border-emerald-400" data-testid={`sdr-resume-${r.id}`}>
                        <i className="fa-solid fa-play" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {convo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConvo(null)}>
          <div className="bg-surface rounded-2xl border border-border w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="sdr-thread-modal">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div>
                <p className="font-black text-text-primary">{convo.enrollment.lead_name}</p>
                <p className="text-[11px] text-text-muted">
                  {convo.enrollment.status} · {convo.enrollment.touches} touches
                  {convo.next_step ? ` · next: ${convo.next_step.channel} #${convo.next_step.step}` : ' · cadence finished'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => act(convo.enrollment.id, 'advance')} className="px-3 py-1.5 rounded-lg bg-primary text-white text-[11px] font-bold" data-testid="sdr-advance-btn">
                  <i className="fa-solid fa-forward mr-1.5" />Send next touch
                </button>
                <button onClick={() => setConvo(null)} className="w-8 h-8 rounded-lg bg-surface-alt border border-border" data-testid="sdr-close-thread">
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="sdr-messages">
              {(convo.messages || []).length === 0 && <p className="text-sm text-text-muted text-center py-8">No touches sent yet.</p>}
              {(convo.messages || []).map(m => (
                <div key={m.id} className={`max-w-[80%] p-3 rounded-2xl text-sm ${m.direction === 'out' ? 'ml-auto bg-primary/10 border border-primary/20' : 'bg-surface-alt border border-border'}`}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">
                    <i className={`fa-solid ${CHANNEL_ICON[m.channel] || 'fa-comment'} mr-1.5`} />
                    {m.direction === 'out' ? 'AI SDR' : 'Homeowner'} · {m.created_at?.slice(5, 16).replace('T', ' ')}
                    {m.delivery?.status === 'simulated' && <span className="ml-2 text-amber-600">simulated</span>}
                  </p>
                  {m.subject ? <p className="font-bold text-text-primary mb-1">{m.subject}</p> : null}
                  <p className="whitespace-pre-wrap text-text-primary">{m.body}</p>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border">
              <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Paste a homeowner reply — the AI reads it, answers it and books if they're ready</p>
              <div className="flex gap-2">
                <input value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder="e.g. Sure, how about Thursday evening?"
                  className="input-field flex-1" data-testid="sdr-reply-input" />
                <button onClick={sendReply} disabled={replying} className="px-5 py-2.5 rounded-xl bg-text-primary text-surface text-sm font-bold disabled:opacity-60" data-testid="sdr-reply-btn">
                  {replying ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-wand-magic-sparkles mr-2" />Handle it</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
